import { useState, useEffect, useMemo, useCallback } from "react"
import {
  useGetBundleColumnsQuery,
  useUpdateBundleColumnsMutation,
  useApplyColumnTemplateMutation,
  useGetColumnTemplatesQuery,
  useGetColumnTemplateDepartmentsQuery,
  useCreateColumnTemplateMutation,
  useUpdateColumnTemplateMutation,
  useDeleteColumnTemplateMutation,
} from "../../../../app/services/book/bundle"

export function useColumnSettings() {
  const {
    data: serverColumns,
    isSuccess,
    isLoading: columnsQueryLoading,
  } = useGetBundleColumnsQuery()

  const [updateBundleColumns] = useUpdateBundleColumnsMutation()
  const [applyColumnTemplateMutation] = useApplyColumnTemplateMutation()

  const [colOrder, setColOrder] = useState([])
  const [visibleCols, setVisibleCols] = useState([])
  const [appliedTemplateId, setAppliedTemplateId] = useState(null)

  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!isSuccess || !serverColumns) return
    setColOrder(
      serverColumns.order?.length ? serverColumns.order : serverColumns.available.map((c) => c.id)
    )
    setVisibleCols(serverColumns.active?.length ? serverColumns.active : serverColumns.defaults)
    setAppliedTemplateId(serverColumns.applied_template_id ?? null)
    setInitialized(true)
  }, [isSuccess, serverColumns])

  const isLoading = columnsQueryLoading || (isSuccess && !initialized)

  const columns = useMemo(() => serverColumns?.available ?? [], [serverColumns])

  const { data: templates = [], isLoading: templatesLoading } = useGetColumnTemplatesQuery()

  const { data: departments = [] } = useGetColumnTemplateDepartmentsQuery()

  const [createTemplateMutation] = useCreateColumnTemplateMutation()
  const [updateTemplateMutation] = useUpdateColumnTemplateMutation()
  const [deleteTemplateMutation] = useDeleteColumnTemplateMutation()

  const applyColumnSettings = useCallback(
    async (order) => {
      setColOrder(order)
      try {
        await updateBundleColumns({ order }).unwrap()
      } catch (e) {
        console.error("Ошибка сохранения порядка колонок", e)
        throw e
      }
    },
    [updateBundleColumns]
  )

  const applyTemplate = useCallback(
    async (template) => {
      try {
        await applyColumnTemplateMutation({ templateId: template.id }).unwrap()
      } catch (e) {
        console.error("Ошибка применения шаблона колонок", e)
        throw e
      }
      setColOrder(template.order)
      setVisibleCols(template.visible)
      setAppliedTemplateId(template.id)
    },
    [applyColumnTemplateMutation]
  )

  const createTemplate = useCallback(
    async (data) => {
      try {
        return await createTemplateMutation(data).unwrap()
      } catch (e) {
        console.error("Ошибка создания шаблона", e)
        throw e
      }
    },
    [createTemplateMutation]
  )

  const updateTemplate = useCallback(
    async (id, data) => {
      try {
        await updateTemplateMutation({ id, ...data }).unwrap()
      } catch (e) {
        console.error("Ошибка обновления шаблона", e)
        throw e
      }
    },
    [updateTemplateMutation]
  )

  const deleteTemplate = useCallback(
    async (id) => {
      try {
        await deleteTemplateMutation(id).unwrap()
        if (String(appliedTemplateId) === String(id)) setAppliedTemplateId(null)
      } catch (e) {
        console.error("Ошибка удаления шаблона", e)
        throw e
      }
    },
    [deleteTemplateMutation, appliedTemplateId]
  )

  return {
    columns,
    colOrder,
    visibleCols,
    applyColumnSettings,
    isLoading,
    templates,
    templatesLoading,
    departments,
    appliedTemplateId,
    applyTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  }
}
